import type { ConfigureAffordance } from './charts/shared';
import { gridBottom } from './grid';
import { resizeItem } from './layout';
import { componentSize, type ComponentRegistry } from './registry';
import type { GridLayout } from './types';

/** Empty grid rows shown beneath the items while editing, to drop or grow into. */
const EDIT_ROWS_BENEATH = 4;
/** The rows an empty canvas shows while editing. */
export const EMPTY_GRID_ROWS = 6;

/** A drag's landing, keyed by the cell it lands in: '' when it lands nowhere. */
export function landingKey(
  landed: { layout: GridLayout; id: string } | null,
): string {
  const place = landed?.layout.find((item) => item.id === landed.id);
  return place && landed
    ? `${landed.id}:${place.x},${place.y},${place.w},${place.h}`
    : '';
}

/** The layout with item `id` resized, no smaller than its type allows. */
export function resizedLayout(
  registry: ComponentRegistry,
  base: GridLayout,
  id: string,
  size: { w: number; h: number },
): GridLayout {
  const item = base.find((entry) => entry.id === id);
  return item
    ? resizeItem(base, id, size, componentSize(registry, item.component.type))
    : base;
}

/**
 * The layout the canvas draws: where a drag would leave it, else as a
 * resize in progress would, else as it is.
 */
export function shownLayout(
  registry: ComponentRegistry,
  layout: GridLayout,
  dragged: GridLayout | null,
  resizing: { id: string; w: number; h: number } | null,
): GridLayout {
  if (dragged) return dragged;
  return resizing
    ? resizedLayout(registry, layout, resizing.id, resizing)
    : layout;
}

/** The grid's height in rows: its items, plus room beneath while editing. */
export function canvasRows(shown: GridLayout, editing: boolean): number {
  if (!shown.length) return EMPTY_GRID_ROWS;
  return gridBottom(shown) + (editing ? EDIT_ROWS_BENEATH : 0);
}

/** How a canvas item says it's set up: by selecting it, its gear, or not here. */
export function configureAffordance(
  selectable: boolean,
  configurable: boolean,
): ConfigureAffordance {
  if (selectable) return 'select';
  return configurable ? 'gear' : 'none';
}
